import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { faArrowLeft, faSearch } from '@fortawesome/pro-light-svg-icons';
import { tv } from 'tailwind-variants';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Input } from '@/components/ui/input/input.component';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { SheetHeader } from '@/components/ui/sheet';
import { useDebounceValue } from '@/hooks/use-debounce.hook';
import { useIsOnMobileDevice } from '@/hooks/use-is-on-mobile-device.hook';
import { useLoading } from '@/hooks/use-loading.hook';
import { useSearchState } from '@/store/search/search.store';
import { MostSearchedResult, MostSearchedResultType, SearchType } from '@/types/most-search-type';
import { getMostSearches } from '@/utils/consts/most-searches-procedures/most-searches-procedures';
import { mostSearchesVaccines } from '@/utils/consts/most-searches-procedures/most-searches-vaccines.consts';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { isVaccineSearchMode } from '@/utils/search/search-vaccine.util';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/(pages)/_components/search/search.component';
import { CheckboxProcedure } from '@/app/(main)/_components/search-modal/_components/checkbox-procedure.component';
import { LaboratoryProcedureLoading } from '@/app/(main)/_components/search-modal/_components/loadings/laboratory-procedure-loading.component';
import { searchHealthResourcesByType } from '@/app/(main)/_components/search-modal/_services/search-health-resources-by-type.service';
import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { getLogger } from '@/logger/get-logger';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface SelectMoreExamsProps {
  selectedProcedures: SelectedProcedures[];
  fistProcedureSelected: SelectedProcedures;
  isVaccineSearch?: boolean;
  cleanValues: () => void;
  setSelectProcedures: Dispatch<SetStateAction<SelectedProcedures[]>>;
}

const selectedMoreExamsStyle = tv({
  slots: {
    seeMoreContainer: [
      'data-[show=false]:hidden data-[show=true]:opacity-1',
      'data-[show=false]:animate-fade-out-bottom-procedure data-[show=true]:animate-fade-in-bottom-procedure',
      'transition-all duration-300 rounded-b-md md:max-w-[800px] w-full bg-white',
      'flex justify-between items-center text-center mt-6',
      'self-end',
    ],
  },

  variants: {
    isOnMobileDevice: {
      true: {
        seeMoreContainer: 'pt-[-50px]',
      },
    },
  },
});

interface ResultProps {
  isLoading?: boolean;
  searchResults?: SearchResult[];
  searchType: SearchType;
  isChecked: (procedure: MostSearchedResult | SearchResult | SelectedProcedures) => boolean;
  handleSelectProcedure: (procedure: MostSearchedResult | SearchResult | SelectedProcedures) => void;
}

export function Results({ isLoading, searchResults, searchType, handleSelectProcedure, isChecked }: ResultProps) {
  const hasSearchResults = (searchResults?.length ?? 0) > 0;

  const mostSearched: MostSearchedResultType
    = searchType === SearchType.VACCINE
      ? mostSearchesVaccines
      : getMostSearches().find((item) => item.type === searchType) ?? ({} as MostSearchedResultType);

  if (isLoading) {
    return <LaboratoryProcedureLoading />;
  }

  if (hasSearchResults) {
    return searchResults?.map((procedure) => (
      <CheckboxProcedure
        key={procedure.id || procedure.code}
        procedure={procedure}
        handleSelectProcedure={handleSelectProcedure}
        isChecked={isChecked}
      />
    ));
  }

  return mostSearched?.procedures.map((procedure) => (
    <CheckboxProcedure
      key={procedure.id}
      procedure={procedure}
      handleSelectProcedure={handleSelectProcedure}
      isChecked={isChecked}
    />
  ));
}

interface SelectMoreExamsProps {
  selectedProcedures: SelectedProcedures[];
  fistProcedureSelected: SelectedProcedures;
  cleanValues: () => void;
  setSelectProcedures: Dispatch<SetStateAction<SelectedProcedures[]>>;
  locationOverride?: OverridedUserLocation;
}

export function SelectMoreExams({
  selectedProcedures,
  fistProcedureSelected,
  isVaccineSearch,
  cleanValues,
  setSelectProcedures,
}: SelectMoreExamsProps) {
  const { handleJump: jump } = useProgressiveModal<ProgressiveSearchModalKeys>();

  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [emptySearchResults, setEmptySearchResults] = useState(false);
  const [page, setPage] = useState(1);
  const [totalResult, setTotalResult] = useState(0);
  const [showSeeSchedules, setShowSeeSchedules] = useState(true);
  const {
    isLoading: isLoadingLaboratoryProcedures,
    startLoading: startLoadingLaboratoryProcedures,
    stopLoading: stopLoadingLaboratoryProcedures,
  } = useLoading();
  const {
    isLoading: isLoadingMoreLaboratoryProcedures,
    startLoading: startLoadingMoreLaboratoryProcedures,
    stopLoading: stopLoadingMoreLaboratoryProcedures,
  } = useLoading();
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRequestId = useRef(0);
  const lastTrackedSearchTerm = useRef<string>('');
  const analyticsInputValueRef = useRef<string>('');

  const { seeMoreContainer } = selectedMoreExamsStyle();

  const isOnMobileDevice = useIsOnMobileDevice();

  const inputValue = useDebounceValue(searchDisplayValue, 400);
  const analyticsInputValue = useDebounceValue(searchDisplayValue, 1000);
  const analytics = getAnalyticsPlugin();

  useEffect(() => {
    analyticsInputValueRef.current = analyticsInputValue;
  }, [analyticsInputValue]);

  const hasSearchResults = searchResults.length > 0;
  const isSearching = inputValue.length > 0;

  const hasNoMoreResults = searchResults.length === totalResult;

  const isVaccine = isVaccineSearchMode(useSearchState.getState().selectedSearch, isVaccineSearch);
  const searchType = isVaccine ? SearchType.VACCINE : SearchType.LABORATORIAL_EXAM;
  const searchInputPlaceholder = isVaccine ? 'Busque pela vacina' : 'Busque pelo exame';
  const addMoreExamsMessage = isVaccine ? 'Acrescente mais vacinas.' : 'Acrescente mais exames laboratoriais.';
  const selectionExamsMessage = isVaccine
    ? 'Selecione as vacinas que deseja.'
    : 'Selecione os exames laboratoriais que deseja realizar.';
  const showMoreExamsTitleButton = isVaccine ? 'Ver mais vacinas' : 'Ver mais exames';
  const selectedExamsCount
    = selectedProcedures.length === 1 ? '1 selecionado' : `${selectedProcedures.length} selecionados`;

  function handleJump(key: ProgressiveSearchModalKeys, returning: boolean = false) {
    const hasSelectedProcedures = selectedProcedures.filter((item) => item.id !== fistProcedureSelected.id).length > 0;

    if (jump) {
      if (!hasSelectedProcedures) {
        jump({ key, returning });

        if (returning && cleanValues && key !== ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE) {
          cleanValues();
        }
      } else {
        jump({ key: ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, returning: true });
      }

      return;
    }
  }

  function handleSelectProcedure(procedure: MostSearchedResult | SearchResult | SelectedProcedures) {
    analytics.track(AnalyticsEnum.VIEW_SEARCH_SELECT_MORE_EXAMES);
    if (selectedProcedures.find((item) => item.id === procedure.id)) {
      setSelectProcedures((prev) => prev.filter((item) => item.id !== procedure.id));
      return;
    }

    if ('id' in procedure && selectedProcedures.some((item) => item.id === procedure.id)) {
      return;
    }

    setSelectProcedures((prev) => {
      if (prev.some((item) => item.id === procedure.id)) {
        return prev;
      }

      return [
        ...prev,
        {
          id: procedure.id || '',
          code: procedure.code || '',
          displayName: procedure.displayName || '',
        },
      ];
    });
  }

  const isChecked = useCallback(
    (procedure: MostSearchedResult | SearchResult | SelectedProcedures) => {
      return selectedProcedures.some((item) => item.id === procedure.id);
    },
    [selectedProcedures],
  );

  function handleSearchInput(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setSearchDisplayValue(value);
  }

  const fetchSearchResults = useCallback(async (page: number = 1, requestId: number, searchTerm: string) => {
    try {
      const { results, total } = await searchHealthResourcesByType(searchTerm, searchType, page);

      if (requestId !== activeRequestId.current) {
        return;
      }

      if (page === 1) {
        const isCurrentSearch = searchTerm === analyticsInputValueRef.current;
        const isNewSearchTerm = lastTrackedSearchTerm.current !== searchTerm;

        if (results.length === 0) {
          if (isCurrentSearch && isNewSearchTerm) {
            analytics.track(AnalyticsEnum.SEARCHED_PROCEDURES_MODAL_WITHOUT_RESULTS, {
              term: searchTerm,
            });
            lastTrackedSearchTerm.current = searchTerm;
          }
          setEmptySearchResults(true);
          return;
        }

        if (results.length > 0 && isCurrentSearch && isNewSearchTerm) {
          analytics.track(AnalyticsEnum.SEARCHED_PROCEDURES_MODAL_WITH_RESULTS, {
            term: searchTerm,
          });
          lastTrackedSearchTerm.current = searchTerm;
        }
      }

      if (page > 1 && results.length === 0) {
        return;
      }

      setTotalResult(total);
      setSearchResults((prev) => [...prev, ...results]);
    } catch (error) {
      if (requestId === activeRequestId.current) {
        const logger = getLogger('SearchModal - SelectMoreExams', {});
        logger.error('Error while fetching Search Results', { lastFetchedPage: page, error });
        setEmptySearchResults(true);
      }
    }
  }, [searchType]);

  async function handleLoadMore() {
    const newPage = page + 1;
    const currentRequestId = activeRequestId.current;

    setPage(newPage);

    startLoadingMoreLaboratoryProcedures();

    await fetchSearchResults(newPage, currentRequestId, inputValue).finally(() => {
      if (currentRequestId === activeRequestId.current) {
        stopLoadingMoreLaboratoryProcedures();
      }
    });
  }

  useEffect(() => {
    setPage(1);
    setSearchResults([]);
    setEmptySearchResults(false);

    if (inputValue.length === 0) {
      lastTrackedSearchTerm.current = '';
      return;
    }

    if (inputValue.length > 0 && inputValue.length <= 2) {
      return;
    }

    const currentRequestId = ++activeRequestId.current;
    const searchTerm = inputValue;

    startLoadingLaboratoryProcedures();

    fetchSearchResults(1, currentRequestId, searchTerm).finally(() => {
      if (currentRequestId === activeRequestId.current) {
        stopLoadingLaboratoryProcedures();
      }
    });

    const activeRequestIdSnapshot = activeRequestId.current;
    return () => {
      if (currentRequestId === activeRequestIdSnapshot) {
        stopLoadingLaboratoryProcedures();
      }
    };
  }, [inputValue, searchType]);
  useEffect(() => {
    const { current } = inputRef;

    if (!current) {
      return;
    }

    const onFocus = () => {
      if (isOnMobileDevice) {
        setShowSeeSchedules(false);
      }
    };

    const onBlur = () => {
      if (isOnMobileDevice) {
        setShowSeeSchedules(true);
      }
    };

    current?.addEventListener('focus', onFocus);
    current?.addEventListener('blur', onBlur);

    return () => {
      current?.removeEventListener('focus', onFocus);
      current?.removeEventListener('blur', onBlur);
    };
  }, [inputRef, isOnMobileDevice]);

  return (
    <div className="flex flex-col justify-between h-full p-5">
      <div>
        <SheetHeader className="flex flex-row items-start gap-4 mb-4">
          <button
            data-testid="back-button--select-more-exams"
            className="pt-5"
            onClick={() => {
              if (selectedProcedures.length === 0) {
                handleJump(ProgressiveSearchModalKeys.SEARCH, true);
                return;
              }

              handleJump(ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, true);
            }}
          >
            <Icon icon={faArrowLeft} size="xl" className="text-gray-500 self-center cursor-pointer" />
          </button>
          <Input
            data-testid="search-input--select-more-exams"
            placeholder={searchInputPlaceholder}
            startIcon={faSearch}
            startIconStyle="text-green-500 text-xl"
            value={searchDisplayValue}
            onChange={handleSearchInput}
            ref={inputRef}
            className="w-full"
          />
        </SheetHeader>
        {emptySearchResults && (
          <p className="text-base font-gray-800 mt-6 text-left" data-testid="search-empty-results--select-more-exams">
            Não encontramos resultados para
            {' '}
            <strong>{searchDisplayValue}</strong>
            . Tente outra palavra.
          </p>
        )}
      </div>

      {!emptySearchResults && (
        <>
          {!hasSearchResults && !isSearching && (
            <>
              {isChecked(fistProcedureSelected)
                ? (
                    <p className="text-gray-800" data-testid="select-procedure-paragraph--select-more-exams">
                      Você selecionou
                      {' '}
                      <strong>{fistProcedureSelected.displayName}</strong>
                      .
                      {' '}
                      {addMoreExamsMessage}
                    </p>
                  )
                : (
                    <p className="text-gray-800" data-testid="select-procedure-paragraph--select-more-exams">
                      {selectionExamsMessage}
                    </p>
                  )}
              {!hasSearchResults && !isSearching && <span className="mt-6">Sugestões:</span>}
            </>
          )}

          <div className="flex flex-col md:basis-[40vh] -mx-5 overflow-y-scroll h-full">
            <Results
              isLoading={isLoadingLaboratoryProcedures}
              searchResults={searchResults}
              searchType={searchType}
              handleSelectProcedure={handleSelectProcedure}
              isChecked={isChecked}
            />

            {!hasNoMoreResults && hasSearchResults && !isLoadingLaboratoryProcedures && (
              <div className="px-5 mt-5">
                <Button
                  data-testid="load-more--select-more-exams"
                  variant="secondary"
                  isLoading={isLoadingMoreLaboratoryProcedures}
                  disabled={isLoadingMoreLaboratoryProcedures}
                  onClick={() => handleLoadMore()}
                >
                  {showMoreExamsTitleButton}
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      <div
        data-testid="see-more-container--select-more-exams"
        data-show={selectedProcedures.length > 0 && showSeeSchedules}
        className={seeMoreContainer({
          isOnMobileDevice: showSeeSchedules && selectedProcedures.length > 0,
        })}
      >
        <span>{selectedExamsCount}</span>
        <Button
          data-testid="see-schedules-button--select-more-exams"
          className="w-fit"
          onClick={() => jump({ key: ProgressiveSearchModalKeys.SELECTED_OVERVIEW_EXAMS })}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
